import { range } from '@app/utils/array';
import { C5Helper } from '@app/utils/c5-helper';
import { StringMap } from './string-map';

/**
 * Returns a function, that, as long as it continues to be invoked, will not
 * be triggered. The function will be called after it stops being called for
 * N milliseconds. If `immediate` is passed, trigger the function on the
 * leading edge, instead of the trailing.
 * See https://davidwalsh.name/javascript-debounce-function for more information
 *
 * @param func Function to invoke
 * @param wait Time to wait, default 400ms
 * @param immediate If the function will be invoked on leading edge, instead of the trailing. Default `false`
 */
export function debounce<T extends (...args: any) => any>(
  func: T,
  wait: number = 400,
  immediate: boolean = false
): (...args: Parameters<T>) => void {
  let timeout: number | null;

  return function (...args) {
    const context = this;

    const later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };

    const callNow = immediate && !timeout;

    window.clearTimeout(timeout);
    timeout = window.setTimeout(later, wait);

    if (callNow) func.apply(context, args);
  };
}

/**
 * Identity function
 */
export function identity<T>(_: T): T {
  return _;
}

/**
 * Tests if the given object has an id that is not equal to 'null | undefined | 0'.
 * When creating new entries we set the id to a fixed value (mostly it's undefined but sometimes it has to be zero because the backend validates it that way).
 * With this function we can determine if the entry should be created or updated
 * @param toTest Object that will be tested
 */
export function isUpdatedEntry<T extends { id?: Identifier }>(toTest: T | Partial<T>): toTest is T {
  return C5Helper.isNew(toTest) === false;
}

/**
 * Global type for an identifier (varies between different entities and therefore code that's supposed to handle both needs this type definition quite often)
 */
export type Identifier = number | string;

/**
 * Check if the given value is not undefined and not null
 * @param value
 */
export function isDefined(value: any): boolean {
  return typeof value !== 'undefined' && value !== null;
}

/**
 * Check if the given value is exclusively an object (no array)
 * @param item
 */
export function isObject(item: any): item is StringMap<any> {
  return item && typeof item === 'object' && !Array.isArray(item);
}

/**
 * Generate unique IDs for use as pseudo-private/protected names
 * (https://gist.github.com/kenng/3fa1347bd0fe34866f28959fec86d784).
 * Similar in concept to <http://wiki.ecmascript.org/doku.php?id=strawman:names>.
 *
 * The goals of this function are twofold:
 *
 * - Provide a way to generate a string guaranteed to be unique when compared
 *   to other strings generated by this function.
 * - Make the string complex enough that it is highly unlikely to be
 *   accidentally duplicated by hand (this is key if you're using `ID`
 *   as a private/protected name on an object).
 *
 * Use:
 *
 * const privateName = ID();
 * const o = { 'public': 'foo' };
 * o[privateName] = 'bar';
 *
 * @param precision The higher the precision the longer and therefore more unique the random string
 */
export function ID(precision: number = 1) {
  // Math.random should be unique because of its seeding algorithm.
  // Convert it to base 36 (numbers + letters), and grab the first 10 characters
  // after the decimal.
  const unique = () => '_' + Math.random().toString(36).substr(2, 10);

  return range(0, precision).map(unique).join('');
}

/**
 * A function that does nothing
 */
export function noop() {}

export function parseMaybeJson(s: any) {
  let result;

  try {
    result = JSON.parse(String(s));
  } catch (e) {
    result = s;
  }

  return result;
}

export * from './sort/sort-by';

/**
 * Check if {subject} starts with {needle}
 * @param subject String to check
 * @param needle String to search
 */
export function startsWith(subject: string, needle: string): boolean {
  return subject.indexOf(needle) === 0;
}

/**
 * Map an array to an object using a specific key that exists on every subject
 * @param subjects Array to map
 * @param key Key to use or function that returns unique value
 */
export function toMap<T, K extends keyof T>(
  subjects: T[],
  key: K | ((item: T) => string | number)
): StringMap<T> {
  return subjects.reduce((obj: any, item: T) => {
    if (typeof key === 'function') {
      obj[key(item)] = item;
    } else if (typeof key === 'string') {
      obj[item[key]] = item;
    }

    return obj;
  }, {});
}

export function toGroup<T>(subjects: T[], key: string = 'id'): GroupMap<T> {
  const group: GroupMap<T> = new GroupMap<T>();

  for (let i = 0; i < subjects.length; i++) {
    const item = subjects[i];
    const subKey = item[key];

    if (group[subKey] === undefined) {
      group[subKey] = {
        key: subKey,
        peers: [],
      };
    }

    group[subKey].peers.push(item);
  }

  return group;
}

/**
 * Remove all duplicates of an array that contains only value types.
 * Special case of uniqBy with parameters *a* and the identity function.
 * @param a Array to remove duplicates from
 */
export function uniq<T>(a: T[]): T[] {
  return uniqBy(a, identity);
}

/**
 * Remove all duplicates based on a given transformation function.
 *
 * For example: uniqBy([2.1, 2.2, 3.5], Math.floor)
 * @param array Array to remove duplicates from
 * @param transform Transformation function from which the return values will be used to remove duplicates
 */
export function uniqBy<T>(array: T[], transform: (a: T) => any = identity): T[] {
  const filtered = [];
  const compare = [];

  for (let i = 0; i < array.length; i++) {
    const value = array[i];
    const transformedValue = transform(value);

    if (compare.indexOf(transformedValue) === -1) {
      compare.push(transformedValue);
      filtered.push(value);
    }
  }

  return filtered;
}

/**
 * Returns a new list in which a list of entries (equal check is performed by the given function) is replaced by their respective counterparts
 *
 * @param list List to operate on
 * @param entries Entries to update
 * @param compare By default the function will check if the ids are the same
 */
export function updateEntries<T>(
  list: T[],
  entries: T[],
  compare: (a: T, b: T) => boolean = (a: any, b: any) => a.id === b.id
) {
  let newList = [];

  for (const value of list) {
    let replaced: boolean = false;

    for (const entry of entries) {
      if (compare(value, entry)) {
        newList.push(entry);
        replaced = true;

        break;
      }
    }

    if (replaced === false) {
      newList.push(value);
    }
  }

  return newList;
}

/**
 * Returns a new list in which a specific entry (equal check is performed by the given function) is replaced by the given entry
 * If no matching entry is found then the given function is called with the list and the entry to somehow modify the list.
 *
 * @param list List to operate on
 * @param entry Entry to update
 * @param compare By default the function will check if the ids are the same
 * @param fn Function that get's called with the list and the new entry that should be added somehow to the list
 */
export function updateList<T>(
  list: T[],
  entry: T,
  compare: (a: T, b: T) => boolean = (a: any, b: any) => a.id === b.id,
  fn: (list: T[], entry: T) => T[] = (list, entry) => [...list, entry]
) {
  let newList = [];
  let updated = false;

  for (const value of list) {
    if (compare(value, entry)) {
      newList.push(entry);
      updated = true;
    } else {
      newList.push(value);
    }
  }

  // Entry was not found so we call the function that modifies the list somehow (append, prepend, whatever)
  if (updated === false) {
    newList = fn(list, entry);
  }

  return newList;
}

/**
 * Returns a new list in which a specific entry (equal check is performed by the given function) is replaced by the given entry.
 * If no matching entry is found then the entry is appended.
 *
 * @param list List to operate on
 * @param entry Entry to update
 * @param compare By default the function will check if the ids are the same
 */
export function updateOrAppend<T>(
  list: T[],
  entry: T,
  compare: (a: T, b: T) => boolean = (a: any, b: any) => a.id === b.id
) {
  return updateList(list, entry, compare, (list, entry) => [...list, entry]);
}

/**
 * Returns a new list in which a specific entry (equal check is performed by the given function) is replaced by the given entry.
 * If no matching entry is found then the entry is not added to the list.
 *
 * @param list List to operate on
 * @param entry Entry to update
 * @param compare By default the function will check if the ids are the same
 */
export function updateOrDiscard<T>(
  list: T[],
  entry: T,
  compare: (a: T, b: T) => boolean = (a: any, b: any) => a.id === b.id
) {
  return updateList(list, entry, compare, (list) => list);
}

/**
 * Returns a new list in which a specific entry (equal check is performed by the given function) is replaced by the given entry.
 * If no matching entry is found then the entry is prepended.
 *
 * @param list List to operate on
 * @param entry Entry to update
 * @param compare By default the function will check if the ids are the same
 */
export function updateOrPrepend<T>(
  list: T[],
  entry: T,
  compare: (a: T, b: T) => boolean = (a: any, b: any) => a.id === b.id
) {
  return updateList(list, entry, compare, (list, entry) => [entry, ...list]);
}

/**
 * Returns a new list which doesn't contain the given entry (equal check is performed by the given function)
 *
 * @param list List to operate on
 * @param entries Entries to remove
 * @param compare Function that compares two objects
 */
export function withoutEntries<T>(
  list: T[],
  entries: T[],
  compare: (a: T, b: T) => boolean = defaultComparisonFunction
) {
  const newList = [];

  for (const item of list) {
    let remove: boolean = false;

    for (const entry of entries) {
      // Push the entry if it is not equal to the given entry
      if (compare(item, entry) === true) {
        remove = true;
        break;
      }
    }

    if (remove === false) {
      newList.push(item);
    }
  }

  return newList;
}

const defaultComparisonFunction = (a: any, b: any) => {
  let equal;

  if (a != null && typeof a === 'object' && 'id' in a) {
    equal = a.id === b.id;
  } else {
    equal = a === b;
  }

  return equal;
};

/**
 * Returns a new list which doesn't contain the given entry (equal check is performed by the given function)
 *
 * @param list List to operate on
 * @param entry Entry to remove
 * @param compare Function that compares two objects
 */
export function withoutEntry<T>(
  list: T[],
  entry: T | { id: Identifier },
  compare: (a: T, b: T | { id: Identifier }) => boolean = defaultComparisonFunction
) {
  const newList = [];

  for (const item of list) {
    // Push the entry if it is not equal to the given entry
    if (compare(item, entry) === false) {
      newList.push(item);
    }
  }

  return newList;
}

export class GroupMap<Value> {
  /**
   * Get all entries as an array (static because the function could override a possible value [key: string])
   * @param groupMap
   */
  static toArray<Value>(groupMap: GroupMap<Value>): Value[] {
    return Object.keys(groupMap)
      .map((_) => groupMap[_].peers)
      .reduce((a, b) => a.concat(b), []);
  }

  [key: string]: GroupMapEntry<Value>;
}

export type GroupMapEntry<Value> = { key: Identifier; peers: Value[] };

/**
 * Group items by keys that are returned by a function.
 * @param items Array of items.
 * @param selector Function that returns a key to determine the group.
 */
export function groupBy<T, K extends string | number>(items: T[], selector: (item: T) => K): Record<K, T[]>;

/**
 * Group items by keys that are returned by a function.
 * @param items Array of items.
 * @param selector Function that returns a key to determine the group.
 * @param transform Function to transform the entries of the grouped map.
 */
export function groupBy<T, K extends string | number, U>(
  items: T[],
  selector: (item: T) => K,
  transform: (values: T[], key: K) => U
): Record<K, U>;

export function groupBy<T, K extends string | number, U = T[]>(
  items: T[],
  fn: (item: T) => K,
  transform?: (values: T[], key: K) => U
): Record<K, U> {
  const grouped: Record<string | number, T[]> = {};

  for (let item of items) {
    const group = fn(item);

    if (grouped[group] == null) {
      grouped[group] = [];
    }

    grouped[group].push(item);
  }

  let result: Record<K, U>;

  if (transform != null) {
    result = {} as Record<K, U>;

    Object.entries(grouped).forEach(([key, value]) => {
      result[key] = transform(value, key as K);
    });
  } else {
    result = grouped as Record<K, U>;
  }

  return result;
}

export function getFirst<T>(values: T[]): T {
  return values?.[0];
}

/**
 * Usually a function that can be called to dispose of a subscription, a timeout or some other long-running process.
 */
export type Disposable = () => void;

export type Newable<T extends {id: number}> = T & {
  id?: number
}
